import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  OpticalJobSummary,
  PdType,
  PrescriptionSummary,
  PrismBase,
  SalesLineWorkflowStatus,
  SalesOrderDetail,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { inventoryKeys } from '@/modules/inventory/api/inventory.api';
import { salesKeys } from './sales.api';

export const opticalKeys = {
  all: [...salesKeys.all, 'optical'] as const,
  jobs: (orderId: string) => [...opticalKeys.all, 'jobs', orderId] as const,
  prescriptions: (orderId: string) => [...opticalKeys.all, 'prescriptions', orderId] as const,
};

export function useOpticalJobsQuery(orderId: string | undefined) {
  return useQuery({
    queryKey: opticalKeys.jobs(orderId ?? ''),
    queryFn: () => api.get<OpticalJobSummary[]>(`/sales/optical/orders/${orderId ?? ''}/jobs`),
    enabled: Boolean(orderId),
  });
}

export function usePrescriptionsQuery(orderId: string | undefined) {
  return useQuery({
    queryKey: opticalKeys.prescriptions(orderId ?? ''),
    queryFn: () =>
      api.get<PrescriptionSummary[]>(`/sales/optical/orders/${orderId ?? ''}/prescriptions`),
    enabled: Boolean(orderId),
  });
}

/** The optical flow moves stock as well as orders, so both caches are invalidated. */
function useOpticalInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: salesKeys.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
  };
}

/** One eye as the form saves it: every field present, null when blank. */
export interface EyeInput {
  sphere: string | null;
  cylinder: string | null;
  /** Whole degrees; a malformed entry is passed through as typed so the server refuses it. */
  axis: number | string | null;
  add: string | null;
  prism: string | null;
  prismBase: PrismBase | null;
}

/** The whole prescription. The endpoint replaces what is stored, so nothing is ever left out. */
export interface PrescriptionPayload {
  right: EyeInput;
  left: EyeInput;
  rightPdMm: string | null;
  leftPdMm: string | null;
  totalPdMm: string | null;
  pdType: PdType | null;
  notes: string | null;
  sourceMeasurementId: string | null;
}

export function useRecordPrescriptionMutation(orderId: string) {
  const invalidate = useOpticalInvalidation();

  return useMutation({
    mutationFn: (payload: PrescriptionPayload) =>
      api.put<PrescriptionSummary>(`/sales/optical/orders/${orderId}/prescription`, payload),
    onSuccess: invalidate,
  });
}

export function useAdvanceWorkflowMutation(orderId: string) {
  const invalidate = useOpticalInvalidation();

  return useMutation({
    mutationFn: (status: SalesLineWorkflowStatus) =>
      api.post<SalesOrderDetail>(`/sales/optical/orders/${orderId}/workflow`, { status }),
    onSuccess: invalidate,
  });
}

export function useIssueLineStockMutation(orderId: string) {
  const invalidate = useOpticalInvalidation();

  return useMutation({
    mutationFn: (payload: { lineId: string; quantity: number }) =>
      api.post<SalesOrderDetail>(`/sales/optical/orders/${orderId}/issue`, payload),
    onSuccess: invalidate,
  });
}

export function useDeliverOrderMutation(orderId: string) {
  const invalidate = useOpticalInvalidation();

  return useMutation({
    mutationFn: (lines?: { lineId: string; quantity: number }[]) =>
      api.post<SalesOrderDetail>(
        `/sales/optical/orders/${orderId}/deliver`,
        lines ? { lines } : {},
      ),
    onSuccess: invalidate,
  });
}
